import { create } from 'zustand';
import type { PuzzleInfo, PuzzleMoveResult } from '@draughts/shared';
import { GameStatus } from '@draughts/shared';
import { fenToBoardState, boardStateToFen, getAllLegalMoves, applyMove, resolveLineStep } from '@draughts/core';
import * as puzzleApi from '../services/puzzle-api';
// Safe to import from a store: dashboard-cache.ts has no runtime imports of its
// own (its only import is type-only), so this does not pull the lazy
// DashboardPage chunk into the puzzle bundle.
import { markDashboardStale } from '../components/dashboard/dashboard-cache';
import { mostraFestaGiornata } from './festa-giornata';
import type { LocalPuzzle } from '../data/demo-puzzle';
import { trackPuzzleStarted, trackPuzzleSolved, trackPuzzleFailed, trackPuzzleHintUsed, trackFirstPuzzle } from '../utils/analytics';

export type PuzzleStatus = 'idle' | 'loading' | 'playing' | 'correct' | 'incorrect' | 'completed' | 'failed';

/** In-browser puzzle session (landing demo): validated locally, no server/auth. */
interface LocalSession extends LocalPuzzle {
  currentFen: string;
  movesPlayed: number;
}

interface PuzzleStore {
  // State
  puzzle: PuzzleInfo | null;
  nonce: string | null;
  status: PuzzleStatus;
  feedback: 'correct' | 'incorrect' | null;
  opponentMove: { from: number; to: number } | null;
  solution: Array<{ from: number; to: number }> | null;
  ratingChange: number | null;
  newRating: number | null;
  puzzleRating: number | null;
  userRating: number | null;
  /** Consecutive puzzles solved in a row, in-session. NOT the activity streak
   *  the UI calls "Streak" — that one counts days and lives server-side. */
  solvedInARow: number;
  dailySolved: boolean;
  /** Id dei puzzle risolti in questa sessione browser. Overlay ottimistico per
   *  la card daily della dashboard: chiudere l'overlay mobile mostra subito
   *  "Risolto ✓" senza refetch. Sopravvive DELIBERATAMENTE a reset() (che gira
   *  sull'unmount di PuzzlePage / chiusura overlay); si azzera solo al reload. */
  solvedPuzzleIds: Set<number>;
  /** Non-null while an in-browser demo (no auth/server) is active. */
  local: LocalSession | null;

  // Actions
  loadDailyPuzzle: (variant: string) => Promise<void>;
  loadNextPuzzle: (variant: string) => Promise<void>;
  startPuzzle: (puzzleId: number, practice?: boolean) => Promise<void>;
  /** Load a fully client-side demo puzzle (validated locally, no auth). */
  loadLocalPuzzle: (puzzle: LocalPuzzle) => void;
  /**
   * `captures` è opzionale e serve solo a disambiguare: due linee legali possono
   * condividere `(from, to)` e mangiare pezzi diversi. Posizionale e in terza
   * posizione per non toccare i chiamanti che passano due argomenti.
   */
  submitMove: (from: number, to: number, captures?: number[]) => Promise<PuzzleMoveResult | null>;
  giveUp: () => Promise<void>;
  replayPuzzle: () => Promise<void>;
  /** Client-side hint for the demo: the from-square of a winning move. */
  localHint: () => { from: number } | null;
  reset: () => void;
  clearFeedback: () => void;
}

export const usePuzzleStore = create<PuzzleStore>((set, get) => ({
  puzzle: null,
  nonce: null,
  status: 'idle',
  feedback: null,
  opponentMove: null,
  solution: null,
  ratingChange: null,
  newRating: null,
  puzzleRating: null,
  userRating: null,
  solvedInARow: 0,
  dailySolved: false,
  solvedPuzzleIds: new Set<number>(),
  local: null,

  loadDailyPuzzle: async (variant: string) => {
    set({ status: 'loading', local: null });
    const result = await puzzleApi.fetchDailyPuzzle(variant);
    if (!result) {
      set({ status: 'idle' });
      return;
    }
    set({
      puzzle: result.puzzle,
      dailySolved: result.solved,
      status: result.solved ? 'completed' : 'idle',
    });
  },

  loadNextPuzzle: async (variant: string) => {
    set({ status: 'loading', feedback: null, solution: null, ratingChange: null, opponentMove: null, local: null });
    const result = await puzzleApi.fetchNextPuzzle(variant);
    if (!result) {
      set({ status: 'idle' });
      return;
    }
    set({
      puzzle: result.puzzle,
      nonce: result.nonce,
      status: 'playing',
    });
    trackPuzzleStarted(result.puzzle.variant, true);
    trackFirstPuzzle();
  },

  startPuzzle: async (puzzleId: number, practice = false) => {
    set({ status: 'loading', feedback: null, solution: null, ratingChange: null, opponentMove: null, local: null });
    try {
      const result = await puzzleApi.startPuzzle(puzzleId, practice);
      set({
        puzzle: result.puzzle,
        nonce: result.nonce,
        status: 'playing',
      });
      trackPuzzleStarted(result.puzzle.variant, !practice);
      trackFirstPuzzle();
    } catch {
      set({ status: 'idle' });
    }
  },

  loadLocalPuzzle: (puzzle: LocalPuzzle) => {
    const info: PuzzleInfo = {
      id: 0,
      fen: puzzle.fen,
      variant: puzzle.variant,
      playerColor: puzzle.playerColor,
      themes: [],
      rating: 0,
      moveCount: puzzle.moveCount,
    };
    set({
      puzzle: info,
      nonce: 'local',
      status: 'playing',
      feedback: null,
      solution: null,
      opponentMove: null,
      ratingChange: null,
      newRating: null,
      puzzleRating: null,
      solvedInARow: 0,
      dailySolved: false,
      local: { ...puzzle, currentFen: puzzle.fen, movesPlayed: 0 },
    });
    // Landing demo — unrated, no first-puzzle credit (it's a teaser, not the
    // real puzzle feature).
    trackPuzzleStarted(puzzle.variant, false);
  },

  submitMove: async (from: number, to: number, captures?: number[]) => {
    // Client-side demo: validate against the embedded solution/tree via the
    // engine — no server, no auth. Mirrors the server's submitMove flow.
    const local = get().local;
    if (local) {
      const board = fenToBoardState(local.currentFen, local.variant);
      const legal = getAllLegalMoves(board);
      const matched = resolveLineStep(legal, { from, to, ...(captures ? { captures } : {}) });
      const winners = local.solutionTree[boardStateToFen(board)] ?? [];
      const canonical = local.solution[local.movesPlayed * 2];
      const accepted = !!matched && (
        winners.some((w) => w.from === from && w.to === to) ||
        (!!canonical && canonical.from === from && canonical.to === to) ||
        legal.length === 1
      );

      if (!accepted) {
        // Wrong move — the demo stays playable; flash 'incorrect' and let the
        // player retry from the same position (PuzzlePage snaps the board back).
        set({ feedback: 'incorrect' });
        return { correct: false, completed: false };
      }

      const complete = (): PuzzleMoveResult => {
        set({ status: 'completed', feedback: 'correct', solution: local.solution, nonce: null });
        trackPuzzleSolved({ variant: local.variant, rated: false, streak: 0 });
        return { correct: true, completed: true, solution: local.solution };
      };

      let next = applyMove(board, matched!);
      const movesPlayed = local.movesPlayed + 1;
      if ((local.moveCount > 0 && movesPlayed >= local.moveCount) || next.status !== GameStatus.PLAYING) {
        return complete();
      }

      // Opponent reply from the stored line (falls back to any legal move — any
      // reply keeps a proven forced win winnable).
      const stored = local.solution[movesPlayed * 2 - 1];
      const oppLegal = getAllLegalMoves(next);
      const opp = oppLegal.find((m) => stored && m.from === stored.from && m.to === stored.to) ?? oppLegal[0];
      if (!opp) return complete();
      next = applyMove(next, opp);
      if (next.status !== GameStatus.PLAYING) return complete();

      // Con le catture, come la risposta del server: il `PuzzlePage` la ri-risolve
      // contro le proprie mosse legali, e `(from, to)` da solo non identifica una
      // mossa.
      const opponentMove = opp.captures.length > 0
        ? { from: opp.from, to: opp.to, captures: opp.captures.map((c) => c.square) }
        : { from: opp.from, to: opp.to };
      set({
        feedback: 'correct',
        opponentMove,
        local: { ...local, currentFen: boardStateToFen(next), movesPlayed },
      });
      return { correct: true, completed: false, opponentMove };
    }

    const { puzzle, nonce } = get();
    if (!puzzle || !nonce) return null;

    try {
      const result = await puzzleApi.submitPuzzleMove(puzzle.id, from, to, nonce, captures);

      if (result.correct && result.completed) {
        // Only a RATED solve bumps the streak (the server omits ratingChange for
        // unrated attempts: hint used, same-day replay, or practice). This mirrors
        // the server, which leaves current_streak untouched on unrated solves.
        const wasRated = result.ratingChange != null;
        set({
          status: 'completed',
          feedback: 'correct',
          solution: result.solution ?? null,
          // Preserve a rating change from an earlier wrong move: a post-fail
          // solve is unrated (result.ratingChange is null), but the streak/rating
          // already moved on the first miss and should still show at the end.
          ratingChange: result.ratingChange ?? get().ratingChange,
          newRating: result.newRating ?? get().newRating,
          // Preserve the puzzle's updated rating from the earlier rated miss: a
          // post-fail solve is unrated (no puzzleRating returned), but the puzzle
          // already moved on the miss and should still show at the end (mirror of
          // ratingChange/newRating above). `?? null` here wiped it → stale display.
          puzzleRating: result.puzzleRating ?? get().puzzleRating,
          nonce: null,
          solvedInARow: wasRated ? get().solvedInARow + 1 : get().solvedInARow,
          // Marca ottimisticamente questo puzzle come risolto per la card daily
          // della dashboard: chiudere l'overlay mobile la aggiorna subito, senza
          // refetch. Nuova reference del Set -> i subscriber si ri-renderizzano.
          solvedPuzzleIds: new Set(get().solvedPuzzleIds).add(puzzle.id),
        });
        // The solve wrote a puzzle_attempts row, so the home dashboard's
        // activity streak / daily goal / heatmap may have moved. Unconditional,
        // not gated on wasRated: an unrated replay is still recorded as solved
        // and can be the user's first activity of the day. Signalled BEFORE the
        // analytics call — everything here runs inside the try/catch below, and
        // the streak must not depend on the tracker succeeding.
        markDashboardStale();
        /*
         * Lo Streak si è RINNOVATO: il server lo dice solo quando questo
         * tentativo era la prima attività di oggi. Niente obiettivo giornaliero
         * qui — quello conta PARTITE, e un problema non lo muove.
         */
        if (result.streak) mostraFestaGiornata({ giorni: result.streak.current });
        trackPuzzleSolved({
          variant: puzzle.variant,
          rated: wasRated,
          // The analytics property stays named `streak`: its meaning never
          // changed (puzzles in a row), and renaming it would fork the series.
          streak: get().solvedInARow,
          ratingChange: result.ratingChange,
        });
      } else if (result.correct) {
        set({
          feedback: 'correct',
          opponentMove: result.opponentMove ?? null,
          nonce: result.nonce ?? null,
        });
      } else {
        // Wrong move — the puzzle stays playable (retry). Keep the nonce so the
        // next attempt can be submitted and flash 'incorrect' (PuzzlePage clears
        // it on a timer). The first miss already zeroed the streak server-side;
        // any rating drop it returned is kept for the end-of-puzzle summary.
        set({
          feedback: 'incorrect',
          nonce: result.nonce ?? nonce,
          ratingChange: result.ratingChange ?? get().ratingChange,
          newRating: result.newRating ?? get().newRating,
          puzzleRating: result.puzzleRating ?? get().puzzleRating,
          solvedInARow: 0,
        });
      }

      return result;
    } catch {
      return null;
    }
  },

  giveUp: async () => {
    const local = get().local;
    if (local) {
      // Local demo: reveal the embedded solution line, no server call.
      set({ status: 'failed', solution: local.solution, nonce: null, solvedInARow: 0 });
      trackPuzzleFailed(local.variant, 'give-up');
      return;
    }
    /*
     * Un solo «Soluzione» per volta.
     *
     * `status: 'failed'` si scriveva DOPO l'await, quindi il bottone restava
     * premibile per tutto il volo della richiesta. Il secondo colpo partiva su un
     * `nonce` già consumato: la seconda risposta tornava senza `solution`, e lo
     * `set` scriveva `solution: result?.solution ?? null` — CANCELLANDO la
     * soluzione appena mostrata. Bastava anche una sola richiesta fallita.
     *
     * Ora lo stato passa a `'failed'` PRIMA della richiesta (la resa è una scelta
     * già presa: non c'è niente da annullare se la rete cade) e la guardia
     * respinge sia il secondo clic sia una resa su un puzzle già chiuso.
     */
    if (get().status !== 'playing') return;
    set({ status: 'failed' });
    const failedVariant = get().puzzle?.variant;
    const result = await puzzleApi.giveUpPuzzle();
    set({
      status: 'failed',
      // No 'incorrect' feedback here: giving up is a choice, not a wrong move,
      // so we don't flash "wrong" on the board.
      // `?? get().solution`: una risposta senza soluzione (rete caduta, o nonce già
      // consumato) non deve CANCELLARE quella che si sta già mostrando.
      solution: result?.solution ?? get().solution,
      // Giving up after a wrong move is unrated, so the server omits these — keep
      // the values the earlier rated miss already produced (same reasoning as the
      // post-fail solve in submitMove), instead of blanking the end summary.
      ratingChange: result?.ratingChange ?? get().ratingChange,
      newRating: result?.newRating ?? get().newRating,
      puzzleRating: result?.puzzleRating ?? get().puzzleRating,
      nonce: null,
      solvedInARow: 0,
    });
    // Asking for the solution is recorded as an attempt that still counts as
    // activity for the day streak (server: solution_revealed), so the home
    // dashboard's streak / daily goal may have moved. Same reasoning as the
    // solve path in submitMove, and signalled before the analytics call so the
    // streak never depends on the tracker succeeding.
    markDashboardStale();
    // Svelare la soluzione È attività per la striscia, quindi può rinnovarla:
    // il testo del velo parla della striscia, non del problema risolto.
    if (result?.streak) mostraFestaGiornata({ giorni: result.streak.current });
    if (failedVariant) trackPuzzleFailed(failedVariant, 'give-up');
  },

  // Replay the just-finished puzzle interactively. Goes through startPuzzle
  // (practice=false) so the replay is unrated — already attempted today — but
  // still recorded, so re-solving a daily keeps it marked "solved".
  replayPuzzle: async () => {
    const { puzzle, local } = get();
    // Local demo: just reload it in-browser (never touches the server).
    if (local) { get().loadLocalPuzzle(local); return; }
    if (!puzzle) return;
    await get().startPuzzle(puzzle.id);
  },

  localHint: () => {
    const local = get().local;
    if (!local) return null;
    const board = fenToBoardState(local.currentFen, local.variant);
    const winners = local.solutionTree[boardStateToFen(board)];
    if (winners && winners.length > 0) { trackPuzzleHintUsed(local.variant); return { from: winners[0]!.from }; }
    const expected = local.solution[local.movesPlayed * 2];
    if (expected) { trackPuzzleHintUsed(local.variant); return { from: expected.from }; }
    return null;
  },

  reset: () => {
    set({
      puzzle: null,
      nonce: null,
      status: 'idle',
      feedback: null,
      opponentMove: null,
      solution: null,
      ratingChange: null,
      newRating: null,
      puzzleRating: null,
      dailySolved: false,
      local: null,
      // NB: solvedPuzzleIds NON viene azzerato qui. reset() gira sull'unmount di
      // PuzzlePage / chiusura overlay; azzerarlo perderebbe il "Risolto ✓" appena
      // guadagnato sulla card daily (bug mobile). Lo `set` di Zustand fa merge
      // shallow, quindi ometterlo lo preserva. Si azzera solo al reload.
    });
  },

  clearFeedback: () => {
    set({ feedback: null, opponentMove: null });
  },
}));
